import React from "react";
import { Box, Typography } from "@mui/material";

import { RISK_MESSAGE_COLOR_MAP } from "../../constants";

const DEFAULT_RISK_COLOR = "#9E9E9E";
const TRACK_COLOR = "#E0E0E0";

// Speedometer-style arc, open at the bottom. Angles are measured clockwise with
// 0° at the top (12 o'clock) and 180° at the bottom. A `SWEEP` of 280° leaves an
// 80° gap centered at the bottom; `START_ANGLE` places v=0 at the bottom-left and
// v=1 at the bottom-right, symmetric about the top. Tweak `SWEEP` to widen/narrow.
const SWEEP = 280;
const START_ANGLE = 180 + (360 - SWEEP) / 2; // 220°
const SEGMENT_GAP_DEG = 4;

const clamp01 = (v) => Math.min(1, Math.max(0, v));

const polarToCartesian = (cx, cy, r, angleDeg) => {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
};

const describeArc = (cx, cy, r, startAngle, endAngle) => {
  const start = polarToCartesian(cx, cy, r, startAngle);
  const end = polarToCartesian(cx, cy, r, endAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
};

// SVG arcs > 180° (and a degenerate 360° single arc) don't render reliably, so
// split any wide sweep into two ≤180° pieces. Returns 0–2 path `d` strings.
const arcPaths = (cx, cy, r, startAngle, endAngle) => {
  const sweep = endAngle - startAngle;
  if (sweep <= 0.01) return [];
  if (sweep <= 180) return [describeArc(cx, cy, r, startAngle, endAngle)];
  const mid = startAngle + sweep / 2;
  return [
    describeArc(cx, cy, r, startAngle, mid),
    describeArc(cx, cy, r, mid, endAngle),
  ];
};

const valueToAngle = (v) => START_ANGLE + clamp01(v) * SWEEP;

/**
 * Speedometer-style (~280°, open at bottom) risk gauge. Replaces the stacked
 * CircularProgress pattern used across the risk maps.
 *
 * @param {number|null} risk       Risk fraction 0–1 (clamped). null → shows "—", track only.
 * @param {string} message         Key into RISK_MESSAGE_COLOR_MAP; fills in that color (fallback #9E9E9E).
 * @param {number} [binCount]      >= 2 ⇒ arc split into that many gap-separated segments; else continuous.
 * @param {number} [size=100]
 * @param {number} [thickness]     Stroke width; defaults to round(size * 0.11).
 * @param {string} [labelVariant]  MUI Typography variant; defaults h6 (size >= 100) else body1.
 */
const SegmentedRiskGauge = ({
  risk,
  message,
  binCount,
  size = 100,
  thickness,
  labelVariant,
}) => {
  const strokeWidth = thickness ?? Math.round(size * 0.11);
  const cx = size / 2;
  const cy = size / 2;
  const arcRadius = (size - strokeWidth) / 2 - 1;

  const clampedRisk = risk != null ? clamp01(Number(risk)) : null;
  const percentageLabel =
    clampedRisk != null ? `${Math.round(clampedRisk * 100)}%` : "—";
  const resolvedLabelVariant = labelVariant ?? (size >= 100 ? "h6" : "body1");

  const valueColor =
    (message != null && RISK_MESSAGE_COLOR_MAP[message]) || DEFAULT_RISK_COLOR;

  const n = Math.floor(Number(binCount));
  const isSegmented = Number.isFinite(n) && n >= 2;

  // Build a flat list of `{ key, d, color }` arc pieces (track first, then fill).
  const arcs = [];
  const pushArcs = (keyPrefix, startAngle, endAngle, color) => {
    arcPaths(cx, cy, arcRadius, startAngle, endAngle).forEach((d, i) => {
      arcs.push({ key: `${keyPrefix}-${i}`, d, color });
    });
  };

  if (isSegmented) {
    // Never let the gap swallow a whole segment (guards huge binCount).
    const effGap = Math.min(SEGMENT_GAP_DEG, (SWEEP / n) * 0.5);
    for (let i = 0; i < n; i += 1) {
      const segStart = valueToAngle(i / n) + effGap / 2;
      const segEnd = valueToAngle((i + 1) / n) - effGap / 2;
      if (segEnd <= segStart) continue;

      pushArcs(`track-${i}`, segStart, segEnd, TRACK_COLOR);

      if (clampedRisk != null) {
        const fillEnd = Math.min(
          valueToAngle(Math.min(clampedRisk, (i + 1) / n)),
          segEnd,
        );
        if (fillEnd > segStart) {
          pushArcs(`fill-${i}`, segStart, fillEnd, valueColor);
        }
      }
    }
  } else {
    pushArcs("track", valueToAngle(0), valueToAngle(1), TRACK_COLOR);
    if (clampedRisk != null && clampedRisk > 0) {
      pushArcs("fill", valueToAngle(0), valueToAngle(clampedRisk), valueColor);
    }
  }

  return (
    <Box position="relative" display="inline-flex">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {arcs.map((seg) => (
          <path
            key={seg.key}
            d={seg.d}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeWidth}
            strokeLinecap="butt"
          />
        ))}
      </svg>

      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ pointerEvents: "none" }}
      >
        <Typography variant={resolvedLabelVariant} component="div">
          {percentageLabel}
        </Typography>
      </Box>
    </Box>
  );
};

export default SegmentedRiskGauge;
